import L from 'leaflet';
import { OpenStreetMapProvider as GeoOpenStreetMapProvider, JsonProvider } from 'leaflet-geosearch';
import { SearchControlOptions } from 'leaflet-geosearch/dist/SearchControl';
import { EndpointArgument, ProviderOptions, ProviderParams } from 'leaflet-geosearch/dist/providers/provider';
import { LImageOverlay } from 'vue2-leaflet';

export enum MapProviderType {
    None = 0,
    Image = 1,
    OpenStreetMap = 2,
    Mapbox = 3,
    HERE = 4,
    GoogleMaps = 5,
    BingMap = 6
}

export interface IMapProvider {
    formatCurrentCoordinates(coordinates: L.LatLng): string;
    contentTemplate: () => Promise<any>;
    dataContext: any;
    geoSearchOptions: SearchControlOptions;
    mapType: MapProviderType;
    zoom: number;
    maxZoom: number;
    minZoom: number;
    maxBounds: L.LatLngBounds;
    crs: L.CRS;
}

export class GeoHereMapProvider extends JsonProvider {
    constructor(option: ProviderOptions) {
        super(option);
    }

    public override endpoint({ query }: EndpointArgument): string {
        const params: ProviderParams = typeof query === 'string'
            ? { q: query }
            : query;

        const paramString = this.getParamString(params);

        return `https://places.api.here.com/places/v1/autosuggest?${paramString}&result_types=place,address&${this.boundsString()}`;
    }

    public override parse({ data }): any {
        return data.results
            .map(f => ({
                x: f.position[1],
                y: f.position[0],
                label: `${f.title}, ${f.vicinity.replace(/<[^>]*>?/gm, ', ')}`,
                bounds: [
                    [f.bbox ? f.bbox[1] : f.position[0], f.bbox ? f.bbox[0] : f.position[1]],
                    [f.bbox ? f.bbox[3] : f.position[0], f.bbox ? f.bbox[2] : f.position[1]]
                ],
                raw: f
            }));
    }

    private boundsString() {
        if (this.inBounds) {
            let west = this.inBounds.getWest() < -180 ? -180 : this.inBounds.getWest();
            let east = this.inBounds.getEast() > 180 ? 180 : this.inBounds.getEast();

            return `in=${west},${this.inBounds.getSouth()},${east},${this.inBounds.getNorth()}`;
        }

        return 'in=-180,-90,180,90';
    }

    public inBounds: L.LatLngBounds;
}

export class GeoMapboxMapProvider extends JsonProvider {
    constructor(option: ProviderOptions) {
        super(option);
    }

    public endpoint({ query }: EndpointArgument): string {
        const params: ProviderParams = typeof query === 'string'
        ? { q: query }
        : query;

        const paramString = this.getParamString(params);

        return `https://api.mapbox.com/geocoding/v5/mapbox.places/${query}.json?${paramString}`;
    }

    public parse({ data }): any {
        return data.features.map(f => ({
            x: f.center[0],
            y: f.center[1],
            label: f.place_name,
            bounds: [
                [f.bbox ? f.bbox[1] : f.center[1], f.bbox ? f.bbox[0] : f.center[0]],
                [f.bbox ? f.bbox[3] : f.center[1], f.bbox ? f.bbox[2] : f.center[0]]
            ],
            raw: f
        }));
    }
}

export class MapBoxMapProvider implements IMapProvider {
    constructor(options: ProviderOptions) {
        let tokenApi: string = options.params?.tokenApi as string;

        if (!tokenApi || tokenApi.length == 0) {
            throw new Error('ArgumentNullException : tokenApi parameter');
        }

        this.contentTemplate = () => import('vue2-leaflet/dist/components/LTileLayer');
        this.dataContext = {
            url: `https://api.mapbox.com/styles/v1/mapbox/streets-v11/tiles/{z}/{x}/{y}?access_token=${tokenApi}`,
            attribution: '© <a href="https://www.mapbox.com/about/maps/">Mapbox</a> © <a href="http://www.openstreetmap.org/copyright">OpenStreetMap</a> <strong><a href="https://www.mapbox.com/map-feedback/" target="_blank">Improve this map</a></strong>',
            tileSize: 512,
            options: {
                zoomOffset: -1
            }
        };
        this.geoSearchOptions = {
            provider: new GeoMapboxMapProvider({
                params: {
                    access_token: tokenApi
                }
            }),
            showMarker: true,
            showPopup: true,
            retainZoomLevel: true,
            style: 'bar'
        };
    }

    public static createOptions(value: string | { [tokenApi: string]: string} | ProviderOptions): ProviderOptions {
        let token: string = null;

        if (typeof value === 'string') {
            token = value;
        }
        else {
            token = isProviderOptions(value)
                ? value.params?.tokenApi as string
                : value.tokenApi;
        }

        return {
            params: {
                tokenApi: token
            }
        };

        function isProviderOptions(option: any) : option is ProviderOptions {
            return 'params' in option;
        }
    }

    public formatCurrentCoordinates(coordinates: L.LatLng): string {
        return `lat: ${coordinates.lat} | Lng: ${coordinates.lng}`;
    }

    private readonly _southWest: L.LatLng = L.latLng(-90, -180);
    private readonly _northEast: L.LatLng = L.latLng(90, 180);

    public contentTemplate: () => Promise<any>;
    public dataContext: any = null;
    public geoSearchOptions: SearchControlOptions = null;
    public zoom: number = 0;
    public maxZoom: number = 18;
    public minZoom: number = 2;
    public crs: L.CRS = L.CRS.EPSG3857;

    public readonly maxBounds: L.LatLngBounds = L.latLngBounds(this._southWest, this._northEast);
    public readonly mapType: MapProviderType = MapProviderType.Mapbox;
}

export class OpenStreetMapProvider implements IMapProvider {
    constructor() {
        this.contentTemplate = () => import('vue2-leaflet/dist/components/LTileLayer');
        this.dataContext = {
            url: 'https://{s}.tile.osm.org/{z}/{x}/{y}.png',
            attribution: 'Map data &copy; <a href=\'https://www.openstreetmap.org/\'>OpenStreetMap</a> contributors, <a href=\'https://creativecommons.org/licenses/by-sa/2.0/\'>CC-BY-SA</a>'
        };
        this.geoSearchOptions = {
            provider: new GeoOpenStreetMapProvider(),
            showMarker: true,
            showPopup: true,
            retainZoomLevel: true,
            style: 'bar'
        };
    }

    public formatCurrentCoordinates(coordinates: L.LatLng): string {
        return `lat: ${coordinates.lat} | Lng: ${coordinates.lng}`;
    }

    private readonly _southWest: L.LatLng = L.latLng(-90, -180);
    private readonly _northEast: L.LatLng = L.latLng(90, 180);

    public contentTemplate: () => Promise<any> = null;
    public dataContext: any = null;
    public geoSearchOptions: SearchControlOptions = null;
    public zoom: number = 0;
    public maxZoom: number = 18;
    public minZoom: number = 2;
    public crs: L.CRS = L.CRS.EPSG3857;

    public readonly maxBounds: L.LatLngBounds = L.latLngBounds(this._southWest, this._northEast);
    public readonly mapType: MapProviderType = MapProviderType.OpenStreetMap;
}

export class HereMapProvider implements IMapProvider {
    constructor(options: any) {
        if (!options.appId || options.appId.length == 0) {
            throw new Error('ArgumentNullException : appId parameter');
        }
        if (!options.appCode || options.appCode.length == 0) {
            throw new Error('ArgumentNullException : appCode parameter');
        }

        const appId: string = options.appId;
        const appCode: string = options.appCode;

        this.contentTemplate = () => import('vue2-leaflet/dist/components/LTileLayer');
        this.dataContext = {
            url: `https://1.base.maps.api.here.com/maptile/2.1/maptile/newest/normal.day/{z}/{x}/{y}/256/png8?app_id=${appId}&app_code=${appCode}`,
            attribution: `Map data &copy; <a href='https://developer.here.com/products/maps'>HERE ${new Date().getFullYear()}</a>`
        };
        this.geoSearchOptions = {
            provider: new GeoHereMapProvider({
                params: {
                    app_id: appId,
                    app_code: appCode
                }
            }),
            showMarker: true,
            showPopup: true,
            retainZoomLevel: true,
            style: 'bar'
        };
    }

    public static createOptions(appId: string, appCode: string): any {
        return {
            appId: appId,
            appCode: appCode
        };
    }

    public formatCurrentCoordinates(coordinates: L.LatLng): string {
        return `lat: ${coordinates.lat} | Lng: ${coordinates.lng}`;
    }

    private readonly _southWest: L.LatLng = L.latLng(-90, -180);
    private readonly _northEast: L.LatLng = L.latLng(90, 180);

    public contentTemplate: () => Promise<any>;
    public dataContext: any = null;
    public geoSearchOptions: SearchControlOptions = null;
    public zoom: number = 2;
    public maxZoom: number = 18;
    public minZoom: number = 2;
    public crs: L.CRS = L.CRS.EPSG3857;

    public readonly maxBounds: L.LatLngBounds = L.latLngBounds(this._southWest, this._northEast);
    public readonly mapType: MapProviderType = MapProviderType.HERE;
}

export class ImageMapProvider implements IMapProvider {
    constructor(options: any) {
        this.contentTemplate = () => import('vue2-leaflet/dist/components/LImageOverlay');
        this.dataContext = {
            url: options.uriConverter
                ? options.uriConverter('/asset/platformexploremap')
                : '/asset/platformexploremap',
            bounds: this.maxBounds
        }
    }

    public formatCurrentCoordinates(coordinates: L.LatLng): string {
        return `X: ${coordinates.lat.toFixed(3)} | Y: ${coordinates.lng.toFixed(3)}`;
    }

    public onLoaded(e: L.LeafletEvent) {
        const { width, height } = this._getImageSize(e.target as LImageOverlay);
        this.maxBounds = new L.LatLngBounds([[0, 0], [height, width]]);
        this.dataContext.bounds = this.maxBounds;
    }

    private _getImageSize(element: LImageOverlay): { width: number, height: number } {
        const image = element.getElement() as HTMLImageElement;

        return { width: image.naturalWidth, height: image.naturalHeight };
    }

    public contentTemplate: () => Promise<any> = null;
    public dataContext: any = null;
    public geoSearchOptions: SearchControlOptions = null;
    public zoom: number = 0;
    public maxZoom: number = 3;
    public minZoom: number = -4;
    public crs: L.CRS = L.CRS.Simple;
    public maxBounds: L.LatLngBounds = L.latLngBounds([[0, 0], [0, 0]]);

    public readonly mapType: MapProviderType = MapProviderType.Image;
}
